import React from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../ui/Button';
import HomeSection from './HomeSection';
import { communityPostTypeLabel, type HomeCommunityFeedItem } from '../../types/communityPosts';

function formatRelativeTime(value?: string | null): string {
  if (!value) return '';
  const date = new Date(value.includes('T') ? value : value.replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return '';
  const diffMs = Date.now() - date.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 14) return `${days}d ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

interface HomeCommunityFeedProps {
  items: HomeCommunityFeedItem[];
}

export const HomeCommunityFeed: React.FC<HomeCommunityFeedProps> = ({ items }) => {
  const navigate = useNavigate();
  const list = items.slice(0, 8);
  if (!list.length) return null;

  return (
    <HomeSection
      title="From your clubs and events"
      description="Private updates from groups and events you belong to."
      action={
        <Button type="button" variant="ghost" size="sm" onClick={() => navigate('/clubs')}>
          Open clubs
        </Button>
      }
    >
      <ul className="space-y-3 list-none">
        {list.map((item) => (
          <li key={item.id}>
            <button
              type="button"
              onClick={() => navigate(item.href || item.source_href)}
              className="w-full text-left rounded-2xl border border-white/[0.06] bg-[#0c0c0c]/70 px-4 py-3.5 hover:border-white/[0.12] cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400 transition-colors"
            >
              <div className="flex flex-wrap items-center gap-2 mb-1.5">
                <span className="text-[11px] uppercase tracking-wide text-orange-300/80">
                  {item.scope === 'club' ? 'Club' : 'Event'}
                </span>
                <span className="text-[11px] text-gray-500 truncate">{item.source_name}</span>
                <span className="text-[11px] text-gray-600">
                  {communityPostTypeLabel(item.post_type)}
                </span>
                {item.is_pinned ? (
                  <span className="text-[11px] text-orange-300/90">Pinned</span>
                ) : null}
                <span className="text-[11px] text-gray-600 ml-auto tabular-nums">
                  {formatRelativeTime(item.published_at)}
                </span>
              </div>
              {item.title ? (
                <p className="text-white font-medium text-sm sm:text-base mb-1">{item.title}</p>
              ) : null}
              <p className="text-sm text-gray-400 line-clamp-2 leading-relaxed">{item.body_preview}</p>
              <p className="text-xs text-gray-600 mt-2">{item.author_name}</p>
            </button>
          </li>
        ))}
      </ul>
    </HomeSection>
  );
};

export default HomeCommunityFeed;
